// apps/client_diy_webapp/src/pages/qualitative-surveys/components/Recruitment.js
import React, { useEffect, useState } from "react";
import { RiFileTextLine } from "react-icons/ri";
import { useGlobalContext } from "../../../Contexts/GlobalContext";
import RadioCard from "./RadioCard";
import { useProjectUpdate } from "../useProjectUpdate";
import SaveBar from "./Savebar";
import { EDITABLE_STATUSES } from "../qualitativeUtils";


export default function Recruitment({ projectDetails }) {
    const { user } = useGlobalContext();
    const organizationID = user?.organization?._id;
    const projectID = projectDetails?._id;

    const isEditable = EDITABLE_STATUSES.includes(projectDetails?.status);

    // Local form state initialised from server data
    const [source, setSource] = useState(projectDetails?.participantSource?.source ?? "mirats_panel");
    const [criteria, setCriteria] = useState(projectDetails?.recruitment?.recruitmentCriteria ?? "");
    const [reviewMethod, setReviewMethod] = useState(projectDetails?.recruitment?.participantReviewMethod ?? "manual");

    // Sync if parent re-fetches
    useEffect(() => {
        setSource(projectDetails?.participantSource?.source ?? "mirats_panel");
        setCriteria(projectDetails?.recruitment?.recruitmentCriteria ?? "");
        setReviewMethod(projectDetails?.recruitment?.participantReviewMethod ?? "manual");
    }, [projectDetails]);

    const { save, isSaving, saveError, saveSuccess } = useProjectUpdate(organizationID, projectID);

    const handleSave = () =>
        save({
            participantSource: { source },
            recruitment: {
                recruitmentCriteria: criteria,
                participantReviewMethod: reviewMethod,
            },
        });

    return (
        <div className="space-y-6">
            {/* ── Participant Source ────────────────────────────────────── */}
            <div className="space-y-3">
                <div>
                    <h1 className="font-bold text-md">Participant Source</h1>
                    <p className="text-[#667085] text-xs">
                        Select where participants will be recruited from.
                    </p>
                </div>
                <div className="space-y-4">
                    <RadioCard
                        title="Mirats Panel"
                        description="Recruit consumers, professionals, or niche audiences from the Mirats participant network."
                        selected={source === "mirats_panel"}
                        onClick={() => isEditable && setSource("mirats_panel")}
                        disabled={!isEditable}
                    />
                    <div className="space-y-2 cursor-not-allowed">

                        <RadioCard
                            title="Your Own Users and Panel"
                            description="Recruit from your own customer list, internal panel, or uploaded CSV."
                            icon={<RiFileTextLine size={20} />}
                            chipText="Coming Soon"
                            disabled
                        />
                    </div>
                </div>
            </div>

            {/* ── Recruitment Criteria ──────────────────────────────────── */}
            <div className="space-y-3">
                <div>
                    <h1 className="font-bold text-md">Recruitment Criteria</h1>
                    <p className="text-[#667085] text-xs">
                        Describe who you want to recruit. Include demographic, professional, behavioural,
                        technical, or category-specific requirements.
                    </p>
                </div>
                <div className="w-full bg-white border border-gray-300 p-3 rounded-xl">
                    <textarea
                        placeholder={`Example 1:\nWe need working professionals aged 25–40 from metro cities who use food delivery apps at least twice a week.\n\nExample 2:\nWe need IT decision makers from companies with 100+ employees who are involved in software purchasing decisions.\n\nExample 3:\nWe need parents of children aged 3–8 who have purchased educational toys in the last 6 months.`}
                        className="w-full resize-none border border-gray-200 px-[14px] py-[10px] rounded-md text-sm placeholder:text-sm focus:outline-none focus:border-[#0226BE] transition-colors disabled:bg-gray-50 disabled:text-gray-500"
                        rows={8}
                        maxLength={3000}
                        value={criteria}
                        onChange={(e) => isEditable && setCriteria(e.target.value)}
                        disabled={!isEditable}
                    />
                    <div className="flex items-center justify-between mt-1">
                        <p className="text-gray-400 text-xs">{criteria.length}/3000</p>
                        <p className="text-gray-500 text-xs">30–3000 characters required</p>
                    </div>
                </div>
            </div>

            {/* ── Review Method ─────────────────────────────────────────── */}
            <div className="space-y-3">
                <div>
                    <h1 className="font-bold text-md">Review Participants</h1>
                    <p className="text-[#667085] text-xs">
                        Decide whether participants should be manually reviewed or automatically allowed.
                    </p>
                </div>
                <div className="grid grid-cols-2 items-start gap-4">
                    <RadioCard
                        title="Manual"
                        description="Review and approve participants before they are scheduled or invited."
                        selected={reviewMethod === "manual"}
                        onClick={() => isEditable && setReviewMethod("manual")}
                        disabled={!isEditable}
                    />
                    <div className="space-y-1">
                        <RadioCard
                            title="Automatic"
                            description="Allow participants who fully match the criteria to move ahead automatically."
                            selected={reviewMethod === "automatic"}
                            onClick={() => isEditable && setReviewMethod("automatic")}
                            disabled={!isEditable}
                        />
                        {reviewMethod === "automatic" && (
                            <p className="text-amber-600 text-xs font-medium px-1">
                                Recommended only for broad audiences with simple screening criteria.
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Save bar ──────────────────────────────────────────────── */}
            {isEditable && (
                <SaveBar
                    onSave={handleSave}
                    isSaving={isSaving}
                    saveSuccess={saveSuccess}
                    saveError={saveError}
                />
            )}
        </div>
    );
}